import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { useAuth } from '../auth/AuthContext'
import { Roles } from '../auth/rbac'

function formatDate(iso) {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return String(iso)
    return d.toLocaleDateString('ko-KR')
  } catch {
    return String(iso)
  }
}

export default function SupportInquiriesPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const isOperator = useMemo(() => (user?.roles || []).includes(Roles.OPERATOR), [user?.roles])

  // 운영자는 문의를 "작성"하지 않고, 들어온 문의를 처리하는 용도이므로 기본은 전체 조회
  const [scope, setScope] = useState(isOperator ? 'all' : 'mine') // mine|all
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState([])
  const [err, setErr] = useState('')

  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    let ignore = false
    async function load() {
      setLoading(true)
      setErr('')
      try {
        const res = await api.get('/inquiries', { params: { scope } })
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        const list = Array.isArray(payload?.content) ? payload.content : (Array.isArray(payload) ? payload : [])
        if (!ignore) setItems(list)
      } catch (e) {
        console.error(e)
        if (!ignore) setErr('문의 목록을 불러오지 못했습니다.')
      } finally {
        if (!ignore) setLoading(false)
      }
    }
    load()
    return () => { ignore = true }
  }, [scope])

  const onSubmit = async (e) => {
    e.preventDefault()
    if (!title.trim() || !content.trim()) return
    try {
      setCreating(true)
      const res = await api.post('/inquiries', { title: title.trim(), content: content.trim() })
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      const id = payload?.id
      setTitle('')
      setContent('')
      if (id) navigate(`/support/inquiries/${id}`)
      else navigate('/support/inquiries')
    } catch (e2) {
      console.error(e2)
      setErr('문의 등록에 실패했습니다.')
    } finally {
      setCreating(false)
    }
  }

  return (
    <section className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">고객문의</h1>
          <p className="text-slate-600 mt-2 text-sm">
            문의 글/답글은 <span className="font-medium">비공개</span>이며, 작성자와 운영자만 확인할 수 있습니다.
          </p>
        </div>
        <div className="text-xs text-slate-500">
          {user?.name ? <div>로그인: {user.name}</div> : null}
        </div>
      </div>

      {!isOperator && (
        <div className="rounded border p-4 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div className="font-semibold text-sm">문의 작성</div>
            <div className="text-xs text-slate-500">운영자가 답글을 남기면 상태가 ‘답변완료’로 표시됩니다.</div>
          </div>
          <form onSubmit={onSubmit} className="space-y-3">
            <div className="space-y-1">
              <label className="text-sm text-slate-700">제목</label>
              <input className="w-full rounded border px-3 py-2" value={title} onChange={(e) => setTitle(e.target.value)} required />
            </div>
            <div className="space-y-1">
              <label className="text-sm text-slate-700">내용</label>
              <textarea className="w-full rounded border px-3 py-2 min-h-[140px]" value={content} onChange={(e) => setContent(e.target.value)} required />
            </div>
            <div className="flex items-center gap-2">
              <button type="submit" className="rounded bg-brand-navy text-white px-4 py-2 text-sm disabled:opacity-60" disabled={creating}>
                {creating ? '등록 중…' : '문의 등록'}
              </button>
              <Link to="/" className="rounded border px-4 py-2 text-sm hover:bg-slate-50">대시보드</Link>
            </div>
          </form>
          {err && <div className="text-sm text-red-600">{err}</div>}
        </div>
      )}

      <div className="rounded border p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="font-semibold text-sm">문의 목록</div>
          <div className="flex items-center gap-2">
            {isOperator && (
              <select className="rounded border px-2 py-1 text-sm" value={scope} onChange={(e) => setScope(e.target.value)}>
                <option value="mine">내 문의</option>
                <option value="all">전체(운영자)</option>
              </select>
            )}
          </div>
        </div>

        {loading ? (
          <div className="text-sm text-slate-600">불러오는 중…</div>
        ) : (
          <div className="divide-y">
            {items.map((it) => (
              <Link key={it.id} to={`/support/inquiries/${it.id}`} className="block py-3 hover:bg-slate-50 px-2 rounded">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium text-slate-800">{it.title}</div>
                  <div className="text-xs text-slate-500">{formatDate(it.createdAt)}</div>
                </div>
                <div className="flex items-center gap-2 mt-1 text-xs">
                  <span className={`rounded px-2 py-0.5 border ${it.status === 'ANSWERED' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-slate-50 border-slate-200 text-slate-600'}`}>
                    {it.status === 'ANSWERED' ? '답변완료' : '접수'}
                  </span>
                  {typeof it.hasReply === 'boolean' && (
                    <span className="text-slate-500">{it.hasReply ? '답글 있음' : '답글 없음'}</span>
                  )}
                  {isOperator && it.creatorName && (
                    <span className="text-slate-500">작성자: {it.creatorName}</span>
                  )}
                </div>
              </Link>
            ))}
            {items.length === 0 && (
              <div className="py-8 text-center text-sm text-slate-500">문의가 없습니다.</div>
            )}
          </div>
        )}
      </div>
    </section>
  )
}

